import React, { useEffect, useRef, useState } from "react";
import { Palette, X } from "lucide-react";
import { DsIcon } from "./components";
import ProductThemePicker from "./ProductThemePicker";
import {
  PRODUCT_THEMES,
  PRODUCT_THEME_CHANGED_EVENT,
  readStoredProductThemeId,
} from "./themes";

/**
 * Floating theme control — available to every authenticated user.
 * Opens a panel to remapping brand --ds-* tokens only.
 */
export default function ProductThemeFab() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(() => readStoredProductThemeId());
  const panelRef = useRef(null);

  useEffect(() => {
    const sync = (event) => {
      setActiveId(event?.detail?.themeId || readStoredProductThemeId());
    };
    window.addEventListener(PRODUCT_THEME_CHANGED_EVENT, sync);
    return () => window.removeEventListener(PRODUCT_THEME_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointer = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      // Ignore clicks on the FAB itself (handled by its toggle)
      if (e.target?.closest?.("[data-product-theme-fab]")) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  const activeTheme =
    PRODUCT_THEMES.find((t) => t.id === activeId) || PRODUCT_THEMES[0];

  return (
    <div
      className="pointer-events-none fixed bottom-5 z-[80] flex flex-col items-end gap-3"
      style={{
        right: "calc(1.25rem + var(--admin-right-sidebar-width, 0px))",
      }}
    >
      {open ? (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Product theme color"
          className="pointer-events-auto w-[min(100vw-2rem,18rem)] overflow-visible rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-panel)]"
        >
          <div className="mb-3 flex items-start justify-between gap-2">
            <div>
              <p className="ds-caption font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Appearance
              </p>
              <p className="ds-body mt-0.5 font-semibold text-[hsl(var(--ds-fg))]">
                Theme color
              </p>
            </div>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
              aria-label="Close theme picker"
              onClick={() => setOpen(false)}
            >
              <DsIcon icon={X} size="sm" />
            </button>
          </div>
          <ProductThemePicker canEdit compact />
        </div>
      ) : null}

      <button
        type="button"
        data-product-theme-fab
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Change product theme color"
        title={`Theme: ${activeTheme.name}`}
        onClick={() => setOpen((v) => !v)}
        className="pointer-events-auto inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] shadow-[var(--ds-shadow-panel)] transition hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] focus-visible:ring-offset-2"
      >
        <span className="relative">
          <DsIcon icon={Palette} size="md" />
          <span
            className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full border border-[hsl(var(--ds-primary))]"
            style={{ background: activeTheme.swatch.primary }}
            aria-hidden
          />
        </span>
      </button>
    </div>
  );
}
